import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import ErrorToast from '../../components/ui/ErrorToast';
import ChangePasswordModal from '../../components/ui/ChangePasswordModal';
import FranchiseeManager from './components/FranchiseeManager';

let _toastId = 0;
const newToastId = () => `toast-${++_toastId}`;

const DOMAIN = 'scann3r.ca';
const RESERVED_SLUGS = new Set(['admin', 'www', 'api', 'app', 'mail', 'root', 'dashboard']);

/** Persists across route unmounts so revisiting /super-admin does not refetch. */
const restaurantDataCache = {
  restaurants: null,
  memberCounts: {},
  userId: null,
};

const syncRestaurantCache = (restaurants, memberCounts, userId = restaurantDataCache?.userId) => {
  restaurantDataCache.restaurants = restaurants;
  restaurantDataCache.memberCounts = memberCounts;
  restaurantDataCache.userId = userId;
};

const clearRestaurantCache = () => {
  restaurantDataCache.restaurants = null;
  restaurantDataCache.memberCounts = {};
  restaurantDataCache.userId = null;
};

const NETLIFY_STATUS_STYLES = {
  registered: { label: 'Registered', className: 'bg-green-500/10 text-green-600' },
  pending: { label: 'Pending', className: 'bg-amber-500/10 text-amber-600' },
  failed: { label: 'Failed', className: 'bg-destructive/10 text-destructive' },
};

const getNetlifyStatusStyle = (status) =>
  NETLIFY_STATUS_STYLES?.[status] || NETLIFY_STATUS_STYLES?.pending;

const parseFunctionInvokeError = async (error, data) => {
  if (data?.error) return data?.error;
  if (data?.message) return data?.message;

  if (error?.context && typeof error?.context?.json === 'function') {
    try {
      let payload = await error?.context?.json();
      if (payload?.error) return payload?.error;
      if (payload?.message) return payload?.message;
    } catch {
      // fall through to error.message
    }
  }

  if (typeof error?.context?.body === 'string') {
    try {
      let payload = JSON.parse(error?.context?.body);
      if (payload?.error) return payload?.error;
      if (payload?.message) return payload?.message;
    } catch {
      // fall through
    }
  }

  return error?.message || 'Unknown error';
};

const parseFunctionInvokeFailure = async (error, data) => {
  let payload = data;

  if (error?.context && typeof error?.context?.json === 'function') {
    try {
      const contextPayload = await error?.context?.json();
      payload = { ...contextPayload, ...payload };
    } catch {
      // keep existing payload
    }
  }

  const message = payload?.error || (await parseFunctionInvokeError(error, data));
  const rolledBack = payload?.rolled_back === true;

  return {
    message,
    rolledBack,
    detail: payload?.message || null,
  };
};

const normalizeCreatedRestaurant = (data) => {
  if (!data) return null;
  if (Array.isArray(data)) return data?.[0] ?? null;
  return data;
};

/* ─── Create Restaurant Modal ─────────────────────────────────────── */
const CreateRestaurantModal = ({ onClose, onCreated, addToast, restaurants = [] }) => {
  const [form, setForm] = useState({ display_name: '', slug: '', owner_email: '', phone: '', address: '' });
  const [slugAuto, setSlugAuto] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState('');
  const [submitError, setSubmitError] = useState(null);
  const [copyFromId, setCopyFromId] = useState('');

  const handleName = (val) => {
    setSubmitError(null);
    setForm(prev => ({
      ...prev,
      display_name: val,
      slug: slugAuto
        ? val?.toLowerCase()?.replace(/\s+/g, '-')?.replace(/[^a-z0-9-]/g, '')?.replace(/-+/g, '-')?.replace(/^-|-$/g, '')
        : prev?.slug,
    }));
  };

  const handleSlug = (val) => {
    setSlugAuto(false);
    setSubmitError(null);
    setForm(prev => ({ ...prev, slug: val?.toLowerCase()?.replace(/\s+/g, '-')?.replace(/[^a-z0-9-]/g, '')?.replace(/-+/g, '-') }));
  };

  const copyMenuFromRestaurant = async (sourceRestaurantId, targetRestaurantId) => {
    // Fetch dishes from source restaurant
    const { data: sourceDishes, error: fetchError } = await supabase?.from('menu_dishes')?.select('*')?.eq('restaurant_id', sourceRestaurantId);

    if (fetchError) throw new Error(`Failed to fetch source menu: ${fetchError.message}`);
    if (!sourceDishes?.length) return;

    // Fetch categories from source restaurant
    const { data: sourceCategories, error: catFetchError } = await supabase?.from('menu_categories')?.select('*')?.eq('restaurant_id', sourceRestaurantId);

    if (catFetchError) throw new Error(`Failed to fetch source categories: ${catFetchError.message}`);

    // Build a map of old category name -> new category id for the target restaurant
    const categoryNameMap = {};
    if (sourceCategories?.length) {
      // Get existing categories for target restaurant
      const { data: targetCats } = await supabase?.from('menu_categories')?.select('id, name')?.eq('restaurant_id', targetRestaurantId);

      const existingByName = {};
      targetCats?.forEach(c => { existingByName[c.name?.toLowerCase()] = c?.id; });

      // For each source category, find or create in target
      for (const cat of sourceCategories) {
        const key = cat?.name?.toLowerCase();
        if (existingByName?.[key]) {
          categoryNameMap[cat.id] = existingByName?.[key];
        } else {
          const { data: newCat, error: newCatErr } = await supabase?.from('menu_categories')?.insert({ name: cat?.name, restaurant_id: targetRestaurantId, sort_order: cat?.sort_order })?.select('id')?.single();
          if (!newCatErr && newCat?.id) {
            categoryNameMap[cat.id] = newCat?.id;
            existingByName[key] = newCat?.id;
          }
        }
      }
    }

    // Copy dishes with new restaurant_id and mapped category_id
    const dishesToInsert = sourceDishes?.map(({ id, restaurant_id, category_id, created_at, updated_at, scan_count, ...rest }) => ({
      ...rest,
      restaurant_id: targetRestaurantId,
      category_id: category_id && categoryNameMap?.[category_id] ? categoryNameMap?.[category_id] : null,
      scan_count: 0,
    }));

    const { error: insertError } = await supabase?.from('menu_dishes')?.insert(dishesToInsert);

    if (insertError) throw new Error(`Failed to copy dishes: ${insertError.message}`);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form?.display_name?.trim() || !form?.slug?.trim()) {
      addToast('Restaurant name and subdomain are required.');
      return;
    }
    if (RESERVED_SLUGS?.has(form?.slug?.trim())) {
      addToast('This subdomain is reserved. Please choose another one.');
      return;
    }
    setLoading(true);
    setSubmitError(null);
    setLoadingStep('creating');
    try {
      const { data, error } = await supabase?.rpc('create_restaurant_with_defaults', {
        p_name: form?.display_name?.trim(),
        p_slug: form?.slug?.trim(),
        p_owner_email: form?.owner_email?.trim() || null,
        p_phone: form?.phone?.trim() || null,
        p_address: form?.address?.trim() || null,
      });
      if (error) {
        if (error?.code === '23505' || `${error?.message || ''}`?.toLowerCase()?.includes('exists')) {
          setSubmitError({
            title: 'Subdomain already exists',
            message: 'A restaurant with this subdomain already exists. Choose a different subdomain.',
          });
        } else {
          throw error;
        }
        return;
      }
      const created = normalizeCreatedRestaurant(data);
      if (!created?.restaurant_id) {
        throw new Error('Restaurant was created but no ID was returned.');
      }

      setLoadingStep('registering');

      const { data: netlifyData, error: netlifyError } = await supabase?.functions?.invoke(
        'register-restaurant-subdomain',
        {
          body: {
            restaurant_id: created?.restaurant_id,
            slug: created?.restaurant_slug,
            rollback_on_failure: true,
          },
        },
      );

      if (netlifyError || netlifyData?.error) {
        const failure = await parseFunctionInvokeFailure(netlifyError, netlifyData);
        const errorState = {
          title: failure?.rolledBack ? 'Restaurant not created' : 'Subdomain registration failed',
          message: failure?.rolledBack
            ? `${failure?.message}${failure?.detail ? ` ${failure?.detail}` : ' The restaurant was removed from the database because Netlify subdomain registration failed.'}`
            : failure?.message,
        };
        setSubmitError(errorState);
        addToast(errorState?.message, 'error');
        return;
      }

      const netlifyFields = {
        netlify_domain_status: 'registered',
        netlify_domain: netlifyData?.domain || `${created?.restaurant_slug}.${DOMAIN}`,
        netlify_registered_at: new Date()?.toISOString(),
        netlify_last_error: null,
      };

      if (form?.owner_email?.trim() && created?.restaurant_id) {
        const { error: inviteError } = await supabase?.functions?.invoke('invite-restaurant-member', {
          body: {
            restaurant_id: created?.restaurant_id,
            email: form?.owner_email?.trim(),
            role: 'restaurant_admin',
          },
        });
        if (inviteError) {
          addToast(`Restaurant created, but owner invite failed: ${inviteError?.message}`, 'warning');
        }
      }

      // Copy menu from selected restaurant if chosen
      if (copyFromId && created?.restaurant_id) {
        setLoadingStep('copying');
        try {
          await copyMenuFromRestaurant(copyFromId, created?.restaurant_id);
        } catch (copyErr) {
          addToast(`Restaurant created, but menu copy failed: ${copyErr?.message}`, 'warning');
        }
      }

      addToast(`"${created?.restaurant_name}" created successfully!`, 'success');
      onCreated({
        id: created?.restaurant_id,
        slug: created?.restaurant_slug,
        display_name: created?.restaurant_name,
        status: 'active',
        created_at: new Date()?.toISOString(),
        ...netlifyFields,
      });
      onClose();
    } catch (err) {
      const message = err?.message || 'Unknown error';
      setSubmitError({
        title: 'Failed to create restaurant',
        message,
      });
      addToast(`Failed to create restaurant: ${message}`, 'error');
    } finally {
      setLoading(false);
      setLoadingStep('');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-md">
        {/* Modal header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-primary/10 rounded-xl flex items-center justify-center">
              <Icon name="PlusCircle" size={18} className="text-primary" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">Create Restaurant</h2>
              <p className="text-xs text-muted-foreground">Available online after subdomain is registered on Netlify</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
          >
            <Icon name="X" size={16} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Restaurant Name <span className="text-destructive">*</span>
            </label>
            <Input
              value={form?.display_name}
              onChange={(e) => handleName(e?.target?.value)}
              placeholder="e.g. Pizza Place"
              autoFocus
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Subdomain <span className="text-destructive">*</span>
            </label>
            <div className="relative">
              <Input
                value={form?.slug}
                onChange={(e) => handleSlug(e?.target?.value)}
                placeholder="pizza-place"
                required
                className="pr-28"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none select-none">
                .{DOMAIN}
              </span>
            </div>
            {form?.slug && !submitError && (
              <div className="mt-2 flex items-center gap-1.5 px-3 py-2 bg-muted/50 border border-border rounded-lg">
                <Icon name="Globe" size={12} className="text-muted-foreground flex-shrink-0" />
                <span className="text-xs text-foreground font-medium truncate">
                  https://{form?.slug}.{DOMAIN}
                </span>
                <span className="ml-auto text-xs text-muted-foreground flex-shrink-0">Preview URL</span>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Owner Email (optional)</label>
            <Input
              value={form?.owner_email}
              onChange={(e) => setForm(prev => ({ ...prev, owner_email: e?.target?.value }))}
              placeholder="owner@restaurant.com"
              type="email"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Phone (optional)</label>
            <Input
              value={form?.phone}
              onChange={(e) => setForm(prev => ({ ...prev, phone: e?.target?.value }))}
              placeholder="+1 555 123 4567"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Address (optional)</label>
            <Input
              value={form?.address}
              onChange={(e) => setForm(prev => ({ ...prev, address: e?.target?.value }))}
              placeholder="123 Main St"
            />
          </div>

          {/* Copy menu section */}
          {restaurants?.length > 0 && (
            <div className="border border-border rounded-xl p-4 bg-muted/30">
              <div className="flex items-center gap-2 mb-3">
                <Icon name="Copy" size={15} className="text-primary" />
                <span className="text-sm font-medium text-foreground">Copy Menu (optional)</span>
              </div>
              <label className="block text-xs text-muted-foreground mb-1.5">
                Copy all dishes and categories from an existing restaurant
              </label>
              <select
                value={copyFromId}
                onChange={(e) => setCopyFromId(e?.target?.value)}
                className="w-full h-10 px-3 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
              >
                <option value="">— Don't copy, start empty —</option>
                {restaurants?.map(r => (
                  <option key={r?.id} value={r?.id}>
                    {r?.display_name || r?.slug}
                  </option>
                ))}
              </select>
              {copyFromId && (
                <p className="mt-2 flex items-center gap-1.5 text-xs text-primary">
                  <Icon name="CheckCircle" size={12} />
                  Menu will be copied from "{restaurants?.find(r => r?.id === copyFromId)?.display_name || restaurants?.find(r => r?.id === copyFromId)?.slug}"
                </p>
              )}
            </div>
          )}

          {submitError && (
            <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3">
              <div className="flex items-start gap-2">
                <Icon name="AlertCircle" size={16} className="text-destructive flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-destructive">{submitError?.title}</p>
                  <p className="text-xs text-destructive/90 mt-1 leading-relaxed">{submitError?.message}</p>
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <Button type="submit" disabled={loading} className="flex-1 flex items-center justify-center gap-2">
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {loadingStep === 'registering' ? 'Registering subdomain...' : loadingStep === 'copying' ? 'Copying menu...' : 'Creating restaurant...'}
                </>
              ) : (
                <>
                  <Icon name="Save" size={15} />
                  Save Restaurant
                </>
              )}
            </Button>
            <Button type="button" variant="outline" onClick={onClose} className="flex-shrink-0">
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ─── Change Credentials Modal ────────────────────────────────────── */
const ChangeCredentialsModal = ({ restaurant, onClose, addToast }) => {
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form?.email?.trim() && !form?.password?.trim()) {
      setError('Please enter a new email or password.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: fnError } = await supabase?.functions?.invoke(
        'update-restaurant-user-credentials',
        {
          body: {
            restaurant_id: restaurant?.id,
            ...(form?.email?.trim() ? { new_email: form?.email?.trim() } : {}),
            ...(form?.password?.trim() ? { new_password: form?.password?.trim() } : {}),
          },
        },
      );
      if (fnError || data?.error) {
        throw new Error(data?.error || fnError?.message || 'Update failed');
      }
      addToast(`Credentials updated for "${restaurant?.display_name}"`, 'success');
      onClose();
    } catch (err) {
      setError(err?.message || 'Failed to update credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between px-6 py-5 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-primary/10 rounded-xl flex items-center justify-center">
              <Icon name="KeyRound" size={18} className="text-primary" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">Change Credentials</h2>
              <p className="text-xs text-muted-foreground truncate max-w-[180px]">{restaurant?.display_name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
          >
            <Icon name="X" size={16} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <p className="text-xs text-muted-foreground">
            Leave a field blank to keep it unchanged. Only the restaurant admin account will be updated.
          </p>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">New Email</label>
            <Input
              type="email"
              value={form?.email}
              onChange={(e) => setForm(prev => ({ ...prev, email: e?.target?.value }))}
              placeholder="new@email.com"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">New Password</label>
            <Input
              type="password"
              value={form?.password}
              onChange={(e) => setForm(prev => ({ ...prev, password: e?.target?.value }))}
              placeholder="••••••••"
            />
          </div>
          {error && (
            <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3">
              <div className="flex items-start gap-2">
                <Icon name="AlertCircle" size={16} className="text-destructive flex-shrink-0 mt-0.5" />
                <p className="text-sm text-destructive">{error}</p>
              </div>
            </div>
          )}
          <div className="flex gap-3 pt-1">
            <Button type="submit" disabled={loading} className="flex-1 flex items-center justify-center gap-2">
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <Icon name="Save" size={15} />
                  Update Credentials
                </>
              )}
            </Button>
            <Button type="button" variant="outline" onClick={onClose} className="flex-shrink-0">
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ─── Delete Confirm Modal ─────────────────────────────────────────── */
const DeleteConfirmModal = ({ restaurant, onClose, onDeleted, addToast }) => {
  const [loading, setLoading] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  const handleDelete = async () => {
    if (confirmText !== restaurant?.slug) return;
    setLoading(true);
    try {
      const { error: netlifyError } = await supabase?.functions?.invoke('unregister-restaurant-subdomain', {
        body: { restaurant_id: restaurant?.id, slug: restaurant?.slug },
      });
      if (netlifyError) {
        addToast(
          `Could not remove subdomain from Netlify: ${netlifyError?.message || 'Unknown error'}. Deleting restaurant anyway.`,
          'warning',
        );
      }

      const { error } = await supabase?.from('restaurants')?.delete()?.eq('id', restaurant?.id);
      if (error) throw error;
      addToast(`"${restaurant?.display_name}" deleted.`, 'success');
      onDeleted(restaurant?.id);
      onClose();
    } catch (err) {
      addToast('Failed to delete: ' + (err?.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="p-6">
          <div className="w-12 h-12 bg-destructive/10 rounded-xl flex items-center justify-center mx-auto mb-4">
            <Icon name="Trash2" size={22} className="text-destructive" />
          </div>
          <h2 className="text-base font-semibold text-foreground text-center mb-1">Delete Restaurant</h2>
          <p className="text-sm text-muted-foreground text-center mb-5">
            This will permanently delete <strong>{restaurant?.display_name}</strong> and all its data. This cannot be undone.
          </p>
          <div className="mb-4">
            <label className="block text-xs font-medium text-foreground mb-1.5">
              Type <span className="font-mono bg-muted px-1 py-0.5 rounded text-destructive">{restaurant?.slug}</span> to confirm
            </label>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e?.target?.value)}
              placeholder={restaurant?.slug}
              autoFocus
            />
          </div>
          <div className="flex gap-3">
            <Button
              variant="destructive"
              className="flex-1"
              disabled={confirmText !== restaurant?.slug || loading}
              onClick={handleDelete}
            >
              {loading ? (
                <span className="flex items-center gap-2 justify-center">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Deleting...
                </span>
              ) : 'Delete'}
            </Button>
            <Button variant="outline" onClick={onClose} className="flex-shrink-0">Cancel</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Restaurant Row ───────────────────────────────────────────────── */
const RestaurantRow = ({ restaurant, onDelete, addToast, onToggleStatus, onNetlifyUpdated, memberCount = 0 }) => {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [retryLoading, setRetryLoading] = useState(false);
  const [showCredentials, setShowCredentials] = useState(false);
  const url = `https://${restaurant?.slug}.${DOMAIN}`;
  const netlifyStatus = getNetlifyStatusStyle(restaurant?.netlify_domain_status);

  const handleCopy = async () => {
    try {
      await navigator.clipboard?.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      addToast('Could not copy to clipboard');
    }
  };

  const createdDate = restaurant?.created_at
    ? new Date(restaurant.created_at)?.toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' })
    : '—';

  const handleRetryRegister = async () => {
    setRetryLoading(true);
    try {
      const { data, error } = await supabase?.functions?.invoke('register-restaurant-subdomain', {
        body: { restaurant_id: restaurant?.id, slug: restaurant?.slug, rollback_on_failure: false },
      });
      if (error || data?.error) {
        throw new Error(await parseFunctionInvokeError(error, data));
      }
      onNetlifyUpdated?.(restaurant?.id, {
        netlify_domain_status: 'registered',
        netlify_domain: `${restaurant?.slug}.${DOMAIN}`,
        netlify_registered_at: new Date()?.toISOString(),
        netlify_last_error: null,
      });
      addToast(`Subdomain registered for ${restaurant?.display_name}`, 'success');
    } catch (err) {
      onNetlifyUpdated?.(restaurant?.id, {
        netlify_domain_status: 'failed',
        netlify_last_error: err?.message || 'Registration failed',
      });
      addToast(`Registration failed: ${err?.message || 'Unknown error'}`, 'error');
    } finally {
      setRetryLoading(false);
    }
  };

  return (
    <div className="flex items-center gap-4 px-6 py-4 hover:bg-muted/20 transition-colors group">
      {/* Icon + name */}
      <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
        <Icon name="Store" size={18} className="text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-foreground truncate">{restaurant?.display_name}</p>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1 mt-0.5 text-xs text-muted-foreground hover:text-primary transition-colors group/url"
          title="Copy URL"
        >
          <Icon name="Globe" size={11} className="flex-shrink-0" />
          <span className="truncate">{url}</span>
          <Icon
            name={copied ? 'Check' : 'Copy'}
            size={11}
            className={`flex-shrink-0 ml-0.5 opacity-0 group-hover/url:opacity-100 transition-opacity ${copied ? 'text-green-500' : ''}`}
          />
        </button>
      </div>
      {/* Status badge */}
      <span className={`hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium flex-shrink-0 ${restaurant?.status === 'inactive' ? 'bg-amber-500/10 text-amber-600' : 'bg-green-500/10 text-green-600'}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${restaurant?.status === 'inactive' ? 'bg-amber-500' : 'bg-green-500'}`} />
        {restaurant?.status === 'inactive' ? 'Inactive' : 'Active'}
      </span>
      <span className={`hidden lg:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium flex-shrink-0 ${netlifyStatus?.className}`}>
        {netlifyStatus?.label}
      </span>
      {/* Date */}
      <span className="hidden md:flex items-center gap-1.5 text-xs text-muted-foreground flex-shrink-0">
        <Icon name="Calendar" size={12} />
        {createdDate}
      </span>
      <span className="hidden md:flex items-center gap-1.5 text-xs text-muted-foreground flex-shrink-0">
        <Icon name="Users" size={12} />
        {memberCount}
      </span>
      {/* Actions */}
      <div className="flex items-center gap-2 flex-shrink-0">
        <Button
          variant="outline"
          size="sm"
          className="text-xs flex items-center gap-1.5 h-8"
          onClick={() => window.open(url, '_blank')}
          title="Open menu"
        >
          <Icon name="ExternalLink" size={12} />
          <span className="hidden sm:inline">View Menu</span>
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="text-xs flex items-center gap-1.5 h-8"
          onClick={() => navigate(`/restaurant-admin-dashboard?restaurant_id=${restaurant?.id}`)}
          title="Manage restaurant"
        >
          <Icon name="Settings" size={12} />
          <span className="hidden sm:inline">Manage</span>
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="text-xs flex items-center gap-1.5 h-8"
          onClick={() => setShowCredentials(true)}
          title="Change admin credentials"
        >
          <Icon name="KeyRound" size={12} />
          <span className="hidden sm:inline">Credentials</span>
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="text-xs h-8"
          onClick={() => onToggleStatus(restaurant)}
          title="Toggle status"
        >
          {restaurant?.status === 'inactive' ? 'Activate' : 'Deactivate'}
        </Button>
        {restaurant?.netlify_domain_status !== 'registered' && (
          <Button
            variant="outline"
            size="sm"
            className="text-xs h-8"
            onClick={handleRetryRegister}
            disabled={retryLoading}
            title="Register subdomain on Netlify"
          >
            {retryLoading ? '...' : 'Register DNS'}
          </Button>
        )}
        <button
          onClick={() => onDelete(restaurant)}
          className="w-8 h-8 flex items-center justify-center rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors opacity-0 group-hover:opacity-100"
          title="Delete restaurant"
        >
          <Icon name="Trash2" size={14} />
        </button>
      </div>
      {showCredentials && (
        <ChangeCredentialsModal
          restaurant={restaurant}
          onClose={() => setShowCredentials(false)}
          addToast={addToast}
        />
      )}
    </div>
  );
};

/* ─── Main Component ───────────────────────────────────────────────── */
const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const { user, userProfile, isSuperAdmin, signOut, loading, profileLoading } = useAuth();
  const { signIn } = useAuth();

  const [activeTab, setActiveTab] = useState('restaurants');
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  const [restaurants, setRestaurants] = useState(() => restaurantDataCache?.restaurants || []);
  const [restaurantsLoading, setRestaurantsLoading] = useState(() => !restaurantDataCache?.restaurants);
  const [memberCounts, setMemberCounts] = useState(() => restaurantDataCache?.memberCounts || {});
  const [search, setSearch] = useState('');

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);

  const [errorToasts, setErrorToasts] = useState([]);
  const addToast = useCallback((message, type = 'error') =>
    setErrorToasts(prev => [...prev, { id: newToastId(), message, type }]), []);
  const dismissToast = (id) =>
    setErrorToasts(prev => prev?.filter(t => t?.id !== id));

  const loadRestaurants = useCallback(async () => {
    setRestaurantsLoading(true);
    try {
      const [{ data, error }, { data: membersData, error: membersError }] = await Promise.all([
        supabase?.from('restaurants')?.select('*')?.order('created_at', { ascending: false }),
        supabase?.from('restaurant_members')?.select('restaurant_id'),
      ]);
      if (error) throw error;
      if (membersError) throw membersError;
      const nextRestaurants = data || [];
      const counts = (membersData || [])?.reduce((acc, row) => {
        const key = row?.restaurant_id;
        if (!key) return acc;
        acc[key] = (acc?.[key] || 0) + 1;
        return acc;
      }, {});
      syncRestaurantCache(nextRestaurants, counts, user?.id);
      setRestaurants(nextRestaurants);
      setMemberCounts(counts);
    } catch (err) {
      addToast('Failed to load restaurants: ' + (err?.message || 'Unknown error'));
    } finally {
      setRestaurantsLoading(false);
    }
  }, [addToast, user?.id]);

  useEffect(() => {
    if (!user) {
      clearRestaurantCache();
      return;
    }
    if (!isSuperAdmin) return;
    if (restaurantDataCache?.restaurants && restaurantDataCache?.userId === user?.id) {
      setRestaurants(restaurantDataCache?.restaurants);
      setMemberCounts(restaurantDataCache?.memberCounts);
      setRestaurantsLoading(false);
      return;
    }
    clearRestaurantCache();
    loadRestaurants();
  }, [isSuperAdmin, loadRestaurants, user]);

  const handleLogin = async (e) => {
    e?.preventDefault();
    setLoginLoading(true);
    setLoginError('');
    try {
      const { error } = await signIn(loginForm?.email, loginForm?.password);
      if (error) setLoginError(error?.message || 'Invalid credentials');
    } catch {
      setLoginError('Login failed. Please try again.');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleRestaurantCreated = (newRestaurant) => {
    setRestaurants((prev) => {
      const next = [newRestaurant, ...prev];
      syncRestaurantCache(next, memberCounts);
      return next;
    });
  };

  const handleRestaurantDeleted = (id) => {
    setRestaurants((prev) => {
      const next = prev?.filter(r => r?.id !== id);
      syncRestaurantCache(next, memberCounts);
      return next;
    });
  };

  const handleNetlifyUpdated = (id, updates) => {
    setRestaurants((prev) => {
      const next = prev?.map((r) => (r?.id === id ? { ...r, ...updates } : r));
      syncRestaurantCache(next, memberCounts);
      return next;
    });
  };

  const handleBackfillSubdomains = async () => {
    setBackfillLoading(true);
    try {
      const { data, error } = await supabase?.functions?.invoke('backfill-restaurant-subdomains', { body: {} });
      if (error || data?.error) {
        throw new Error(await parseFunctionInvokeError(error, data));
      }
      await loadRestaurants();
      addToast(
        `Backfill complete: ${data?.registered ?? 0} registered, ${data?.failed ?? 0} failed`,
        data?.failed ? 'warning' : 'success',
      );
    } catch (err) {
      addToast(`Backfill failed: ${err?.message || 'Unknown error'}`, 'error');
    } finally {
      setBackfillLoading(false);
    }
  };

  const toggleRestaurantStatus = async (restaurant) => {
    const nextStatus = restaurant?.status === 'inactive' ? 'active' : 'inactive';
    const { error } = await supabase
      ?.from('restaurants')
      ?.update({ status: nextStatus, updated_at: new Date()?.toISOString() })
      ?.eq('id', restaurant?.id);
    if (error) {
      addToast('Failed to update status: ' + (error?.message || 'Unknown error'));
      return;
    }
    setRestaurants((prev) => {
      const next = prev?.map((r) => (r?.id === restaurant?.id ? { ...r, status: nextStatus } : r));
      syncRestaurantCache(next, memberCounts);
      return next;
    });
  };

  const filteredRestaurants = restaurants?.filter(r =>
    r?.display_name?.toLowerCase()?.includes(search?.toLowerCase()) ||
    r?.slug?.toLowerCase()?.includes(search?.toLowerCase())
  );

  /* ── Loading (initial auth/profile only — not on background token refresh) ── */
  if (loading || (profileLoading && !userProfile)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  /* ── Login ── */
  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>
        <div className="w-full max-w-md">
          <div className="bg-card border border-border rounded-2xl p-8 shadow-xl">
            <div className="text-center mb-8">
              <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Icon name="Shield" size={26} className="text-primary" />
              </div>
              <h1 className="text-2xl font-bold text-foreground">Super Admin</h1>
              <p className="text-sm text-muted-foreground mt-1">Platform Management Dashboard</p>
            </div>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Email</label>
                <Input
                  type="email"
                  value={loginForm?.email}
                  onChange={(e) => setLoginForm(prev => ({ ...prev, email: e?.target?.value }))}
                  placeholder="felix.gagnon@scann3r.ca"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Password</label>
                <Input
                  type="password"
                  value={loginForm?.password}
                  onChange={(e) => setLoginForm(prev => ({ ...prev, password: e?.target?.value }))}
                  placeholder="••••••••"
                  required
                />
              </div>
              {loginError && (
                <p className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-lg">{loginError}</p>
              )}
              <Button type="submit" className="w-full" disabled={loginLoading}>
                {loginLoading ? (
                  <span className="flex items-center gap-2 justify-center">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Signing in...
                  </span>
                ) : 'Sign In'}
              </Button>
            </form>
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => setShowChangePassword(true)}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1.5 mx-auto"
              >
                <Icon name="KeyRound" size={14} />
                Change Password
              </button>
            </div>
            <div className="mt-4 p-3 bg-muted/50 rounded-lg">
              <p className="text-xs text-muted-foreground text-center font-medium mb-1">Credentials</p>
              <p className="text-xs text-muted-foreground text-center">felix.gagnon@scann3r.ca / Apple1973</p>
            </div>
          </div>
        </div>
        {showChangePassword && (
          <ChangePasswordModal onClose={() => setShowChangePassword(false)} resetMode={true} />
        )}
      </div>
    );
  }

  /* ── Access Denied — restaurant admins belong on their dashboard ── */
  if (!isSuperAdmin) {
    return <Navigate to="/restaurant-admin-dashboard" replace />;
  }

  /* ── Super Admin Dashboard ── */
  return (
    <div className="min-h-screen bg-background" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-primary/10 rounded-xl flex items-center justify-center">
              <Icon name="Shield" size={18} className="text-primary" />
            </div>
            <div>
              <h1 className="text-base font-bold text-foreground leading-none">Super Admin</h1>
              <p className="text-xs text-muted-foreground">scann3r.ca Platform</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground hidden sm:block truncate max-w-[180px]">{user?.email}</span>
            <Button variant="outline" size="sm" onClick={signOut} className="flex items-center gap-1.5 h-8">
              <Icon name="LogOut" size={13} />
              Sign Out
            </Button>
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 md:px-6 py-8">

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <div className="bg-card border border-border rounded-xl p-5 col-span-1">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Store" size={16} className="text-primary" />
              </div>
              <span className="text-xs font-medium text-muted-foreground">Restaurants</span>
            </div>
            <p className="text-3xl font-bold text-foreground">{restaurants?.filter((r) => r?.status !== 'inactive')?.length}</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-5 col-span-1">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 bg-green-500/10 rounded-lg flex items-center justify-center">
                <Icon name="CheckCircle" size={16} className="text-green-500" />
              </div>
              <span className="text-xs font-medium text-muted-foreground">Active</span>
            </div>
            <p className="text-3xl font-bold text-foreground">{restaurants?.length}</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-5 col-span-2 sm:col-span-2">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 bg-blue-500/10 rounded-lg flex items-center justify-center">
                <Icon name="Globe" size={16} className="text-blue-500" />
              </div>
              <span className="text-xs font-medium text-muted-foreground">Platform Domain</span>
            </div>
            <div className="flex items-center gap-2">
              <p className="text-sm font-semibold text-foreground">{DOMAIN}</p>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500/10 text-blue-600">
                Netlify alias per restaurant
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Each restaurant subdomain is registered on Netlify when created. IONOS wildcard DNS routes traffic to the site.
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-muted/40 border border-border rounded-xl p-1 w-fit">
          <button
            onClick={() => setActiveTab('restaurants')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'restaurants' ? 'bg-card text-foreground shadow-sm border border-border' : 'text-muted-foreground hover:text-foreground'}`}
          >
            <Icon name="Store" size={15} />
            Restaurants
          </button>
          <button
            onClick={() => setActiveTab('franchisees')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'franchisees' ? 'bg-card text-foreground shadow-sm border border-border' : 'text-muted-foreground hover:text-foreground'}`}
          >
            <Icon name="Building2" size={15} />
            Franchisés
          </button>
        </div>

        {/* Tab content */}
        {activeTab === 'restaurants' && (
          <>
        {/* Restaurants table */}
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          {/* Table header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-6 py-4 border-b border-border">
            <div>
              <h2 className="text-base font-semibold text-foreground">Restaurants</h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                {restaurants?.length} restaurant{restaurants?.length !== 1 ? 's' : ''} on the platform
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* Search */}
              <div className="relative">
                <Icon name="Search" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e?.target?.value)}
                  placeholder="Search restaurants..."
                  className="pl-8 pr-3 h-9 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors w-48 text-foreground placeholder:text-muted-foreground"
                />
              </div>
              <Button
                variant="outline"
                onClick={handleBackfillSubdomains}
                disabled={backfillLoading}
                className="flex items-center gap-2 h-9 text-sm flex-shrink-0"
                size="sm"
              >
                <Icon name="RefreshCw" size={15} className={backfillLoading ? 'animate-spin' : ''} />
                Register all subdomains
              </Button>
              <Button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 h-9 text-sm flex-shrink-0"
                size="sm"
              >
                <Icon name="Plus" size={15} />
                Create Restaurant
              </Button>
            </div>
          </div>

          {/* List */}
          {restaurantsLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="flex flex-col items-center gap-3">
                <div className="w-7 h-7 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                <p className="text-sm text-muted-foreground">Loading restaurants...</p>
              </div>
            </div>
          ) : filteredRestaurants?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center px-6">
              <div className="w-14 h-14 bg-muted rounded-2xl flex items-center justify-center mb-4">
                <Icon name="Store" size={24} className="text-muted-foreground" />
              </div>
              {search ? (
                <>
                  <p className="text-sm font-medium text-foreground mb-1">No results for "{search}"</p>
                  <p className="text-xs text-muted-foreground">Try a different name or subdomain.</p>
                </>
              ) : (
                <>
                  <p className="text-sm font-medium text-foreground mb-1">No restaurants yet</p>
                  <p className="text-xs text-muted-foreground mb-4">Click "Create Restaurant" to add the first one.</p>
                  <Button size="sm" onClick={() => setShowCreateModal(true)} className="flex items-center gap-2">
                    <Icon name="Plus" size={14} />
                    Create Restaurant
                  </Button>
                </>
              )}
            </div>
          ) : (
            <div className="divide-y divide-border">
              {filteredRestaurants?.map((restaurant) => (
                <RestaurantRow
                  key={restaurant?.id}
                  restaurant={restaurant}
                  onDelete={setDeleteTarget}
                  addToast={addToast}
                  onToggleStatus={toggleRestaurantStatus}
                  onNetlifyUpdated={handleNetlifyUpdated}
                  memberCount={memberCounts?.[restaurant?.id] || 0}
                />
              ))}
            </div>
          )}
        </div>

        {/* Infrastructure info */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-4">
            <div className="flex gap-3">
              <Icon name="Zap" size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-foreground mb-1">Instant Deployment</p>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Each restaurant is live at <strong>{'{slug}'}.{DOMAIN}</strong> after it is created in the database and registered as a Netlify domain alias.
                </p>
              </div>
            </div>
          </div>
          <div className="bg-green-500/5 border border-green-500/20 rounded-xl p-4">
            <div className="flex gap-3">
              <Icon name="ShieldCheck" size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-foreground mb-1">One-Time Infrastructure</p>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  IONOS wildcard DNS (<strong>*.{DOMAIN}</strong>), Netlify site, Git connection, and primary domain are configured once. Each restaurant adds one Netlify alias automatically.
                </p>
              </div>
            </div>
          </div>
        </div>
          </>
        )}

        {activeTab === 'franchisees' && (
          <FranchiseeManager
            allRestaurants={restaurants}
            addToast={addToast}
            currentUserId={user?.id}
          />
        )}
      </main>
      {/* Modals */}
      {showCreateModal && (
        <CreateRestaurantModal
          onClose={() => setShowCreateModal(false)}
          onCreated={handleRestaurantCreated}
          addToast={addToast}
          restaurants={restaurants}
        />
      )}
      {deleteTarget && (
        <DeleteConfirmModal
          restaurant={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={handleRestaurantDeleted}
          addToast={addToast}
        />
      )}
      {showChangePassword && (
        <ChangePasswordModal onClose={() => setShowChangePassword(false)} resetMode={true} />
      )}
      {/* Toasts */}
      <ErrorToast
        toasts={errorToasts}
        onDismiss={dismissToast}
        autoDismissMs={8000}
      />
    </div>
  );
};

export default SuperAdminDashboard;
